import { useCallback, useEffect, useRef, useState } from "react";
import type { BuilderApi, NewComment, PreviewComment } from "../api/contract";
import { toBuilderError } from "../api/errors";
import { newMessageId, sendOutcome } from "../lib/conversationState";
import type { ConversationController } from "./useConversation";

/**
 * Why comments weren't sent, or couldn't be saved.
 * - `moved`: part of the page a comment points at is gone; those comments are marked.
 * - `busy`: the admin is still on the last request. `limited`: the hourly limit was reached.
 * - `failed`: anything else; nothing was sent.
 * - `unsaved`: a comment couldn't be saved or changed.
 */
export type CommentNotice = "moved" | "busy" | "limited" | "failed" | "unsaved" | "full";

export interface UseCommentsOptions {
  api: BuilderApi;
  siteId: string;
  chat: ConversationController;
  /** Delays between resends of a batch whose outcome is unknown (same message ID). */
  retryDelays?: number[];
}

const DEFAULT_RETRY_DELAYS = [1500, 4000];
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function newCommentId(): string {
  return `comment-${crypto.randomUUID()}`;
}

/**
 * The owner's comments on the preview. The server keeps them (per site) and works out what each one
 * points at; this hook shows them, saves changes, sends a batch as one chat message and re-reads
 * them once the admin's turn has finished, so pins show how each one went.
 */
export function useComments({ api, siteId, chat, retryDelays = DEFAULT_RETRY_DELAYS }: UseCommentsOptions) {
  const [comments, setComments] = useState<PreviewComment[]>([]);
  const [busy, setBusy] = useState<"saving" | "sending" | null>(null);
  const [notice, setNotice] = useState<CommentNotice | null>(null);
  // Comments whose part of the page isn't there any more: found by the canvas, or refused by the server.
  const [moved, setMoved] = useState<ReadonlySet<string>>(() => new Set());
  const epoch = useRef(0);

  const refresh = useCallback(async () => {
    const run = epoch.current;
    try {
      const next = await api.listComments(siteId);
      if (run === epoch.current) setComments(next);
    } catch {
      // Pins are a convenience: the conversation and preview carry on without them.
    }
  }, [api, siteId]);

  useEffect(() => {
    void refresh();
    return () => {
      epoch.current += 1;
      setComments([]);
      setMoved(new Set());
      setNotice(null);
    };
  }, [refresh]);

  // A finished turn resolves the comments it carried.
  const pendingTurn = chat.conversation?.pendingTurnId ?? null;
  const lastTurn = useRef<string | null>(null);
  useEffect(() => {
    if (lastTurn.current !== null && pendingTurn === null) void refresh();
    lastTurn.current = pendingTurn;
  }, [pendingTurn, refresh]);

  const mutate = useCallback(async (change: () => Promise<PreviewComment[]>): Promise<boolean> => {
    const run = epoch.current;
    setBusy("saving");
    setNotice(null);
    try {
      const next = await change();
      if (run === epoch.current) setComments(next);
      return true;
    } catch (caught) {
      if (run === epoch.current) setNotice(toBuilderError(caught).status === 429 ? "full" : "unsaved");
      return false;
    } finally {
      if (run === epoch.current) setBusy(null);
    }
  }, []);

  const create = useCallback((comment: NewComment) => mutate(() => api.createComment(siteId, comment)), [api, siteId, mutate]);
  const update = useCallback((id: string, note: string) => mutate(() => api.updateComment(siteId, id, note)), [api, siteId, mutate]);
  const remove = useCallback((id: string) => mutate(() => api.deleteComment(siteId, id)), [api, siteId, mutate]);

  /** Sends comments as one message; an unknown outcome is resent with the same message ID. */
  const send = useCallback(async (ids: string[]): Promise<boolean> => {
    if (ids.length === 0 || busy) return false;
    const run = epoch.current;
    const messageId = newMessageId();
    setBusy("sending");
    setNotice(null);
    try {
      for (let attempt = 0; ; attempt += 1) {
        try {
          const result = await api.sendComments(siteId, { messageId, ids });
          if (run !== epoch.current) return false;
          if ("moved" in result) {
            setMoved((now) => new Set([...now, ...result.moved]));
            setNotice("moved");
            return false;
          }
          chat.adopt(result);
          await refresh();
          return true;
        } catch (caught) {
          if (run !== epoch.current) return false;
          const outcome = sendOutcome(caught);
          if (outcome === "unknown" && attempt < retryDelays.length) {
            await wait(retryDelays[attempt]!);
            continue;
          }
          if (outcome === "signed-out" || outcome === "not-found") chat.forget(outcome);
          setNotice(outcome === "busy" ? "busy" : outcome === "limited" ? "limited" : "failed");
          return false;
        }
      }
    } finally {
      if (run === epoch.current) setBusy(null);
    }
  }, [api, siteId, busy, chat, refresh, retryDelays]);

  /** What the canvas found missing on the page it shows; only that page's comments are judged. */
  const reportMissing = useCallback((pagePath: string, missing: string[]) => {
    setMoved((now) => {
      const onPage = new Set(comments.filter((comment) => comment.pagePath === pagePath).map((comment) => comment.id));
      const next = new Set([...now].filter((id) => !onPage.has(id)));
      for (const id of missing) if (onPage.has(id)) next.add(id);
      return next.size === now.size && [...next].every((id) => now.has(id)) ? now : next;
    });
  }, [comments]);

  return {
    comments, busy, notice, moved,
    drafts: comments.filter((comment) => comment.state === "draft"),
    refresh, create, update, remove, send, reportMissing,
    dismissNotice: useCallback(() => setNotice(null), []),
  };
}

export type CommentsController = ReturnType<typeof useComments>;
